import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors,
  ArrowLeft,
  Plus,
  CaretLeft,
  CaretRight,
  ClipboardText,
  UsersThree,
  Lightning,
  CheckCircle,
  XCircle,
  Minus,
  Clock,
  Trash,
  PencilSimple,
  CalendarX,
  Calendar,
  UserCircle,
  FloppyDisk,
  Spinner,
  X,
  MagnifyingGlass,
  Phone,
  UserPlus,
  Lock,
  ShieldCheck,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import {
  API, STATUS_OPERACIONAL, DIA_TIPOS, CATEGORIA_CONFIG,
  formatDateBR, isToday,
  HORARIO_INICIO, HORARIO_FIM, SLOT_HEIGHT, generateTimeSlots, getSlotTop, getSlotHeight,
} from './constants';
import { ProfissionaisModal } from './ProfissionaisModal';
import { AjusteGerencialModal, RetroativoBadge, ClienteSearchField } from './GovernanceComponents';
import { HistoricoOperacionalDrawer } from './HistoricoOperacionalDrawer';

const STATUS_ICONS = {
  produziu: Lightning,
  disponivel: CheckCircle,
  ausente: XCircle,
};

// ==================== ATENDIMENTO FORM (normal flow) ====================
const AtendimentoForm = ({ profissionais, selectedDate, onClose, onSuccess, editData }) => {
  const isEdit = !!editData;
  const [form, setForm] = useState({
    cliente_nome: editData?.cliente_nome || '',
    cliente_id: editData?.cliente_id || '',
    profissional_id: editData?.profissional_id || '',
    hora_inicio: editData?.hora_inicio || '10:00',
    servicos: editData?.servicos?.join(', ') || '',
    observacoes: editData?.observacoes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nome.trim() || !form.profissional_id) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        data: selectedDate,
        servicos: form.servicos.split(',').map(s => s.trim()).filter(Boolean),
      };
      const url = isEdit ? `${API}/cod/agendamentos/${editData.id}` : `${API}/cod/agendamentos`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        if (err?.detail?.code === 'TIME_BLOCKED') {
          alert(err.detail.message);
          return;
        }
      }
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="atendimento-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
            {isEdit ? 'Editar Atendimento' : 'Registrar Atendimento'}
          </h2>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <ClienteSearchField
            value={form.cliente_nome}
            onChange={(nome) => setForm(p => ({ ...p, cliente_nome: nome, cliente_id: '' }))}
            onSelect={(c) => setForm(p => ({ ...p, cliente_nome: c.nome, cliente_id: c.id }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional *</label>
              <select data-testid="select-profissional" value={form.profissional_id}
                onChange={(e) => setForm(p => ({ ...p, profissional_id: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              >
                <option value="">Selecione...</option>
                {profissionais.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Horario</label>
              <input data-testid="input-hora" type="time" value={form.hora_inicio}
                onChange={(e) => setForm(p => ({ ...p, hora_inicio: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Servicos</label>
            <input data-testid="input-servicos" type="text" value={form.servicos}
              onChange={(e) => setForm(p => ({ ...p, servicos: e.target.value }))}
              placeholder="Corte, Escova, Coloracao..."
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
              Cancelar
            </button>
            <button type="submit" disabled={saving || !form.cliente_nome.trim() || !form.profissional_id}
              data-testid="btn-salvar-atendimento"
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: '#1B4332' }}
            >
              {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== PERSON STATUS CARD ====================
const PersonCard = ({ person, onStatusChange, onDeleteAtend, onEditAtend, onSituacao, compact, isPastDate }) => {
  const status = person.status_dia || '';
  const stCfg = STATUS_OPERACIONAL[status];
  const Icon = STATUS_ICONS[status] || Minus;
  const atendimentos = person.atendimentos || [];

  return (
    <div
      data-testid={`person-${person.id}`}
      className={`bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden transition-all hover:shadow-sm ${compact ? 'p-3' : 'p-4'} ${isPastDate ? 'opacity-90' : ''}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
          style={{ backgroundColor: person.cor || '#1B4332' }}>
          {person.nome?.charAt(0)?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#18181B] truncate" style={{ fontFamily: 'Outfit' }}>{person.nome}</p>
          {person.horario_chegada && (
            <p className="text-[10px] text-[#A1A1AA] flex items-center gap-1">
              <Clock size={10} weight="regular" /> Chegada: {person.horario_chegada}
            </p>
          )}
        </div>
        {/* Retroactive indicator on status */}
        {person.retroativo_status && (
          <RetroativoBadge justificativa={person.retroativo_justificativa_status} />
        )}
      </div>

      {/* Status Buttons */}
      <div className="flex gap-1.5 mb-3">
        {Object.entries(STATUS_OPERACIONAL).map(([key, cfg]) => {
          const Ic = STATUS_ICONS[key];
          const isActive = status === key;
          return (
            <button
              key={key}
              data-testid={`btn-status-${key}-${person.id}`}
              onClick={() => !isPastDate && onStatusChange(person.id, key)}
              disabled={isPastDate}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-wide transition-all ${
                isActive ? 'text-white shadow-sm' : 'border border-[#E4E4E7] hover:shadow-sm'
              } ${isPastDate ? 'cursor-not-allowed opacity-50' : ''}`}
              style={isActive ? { backgroundColor: cfg.color } : { color: cfg.color, backgroundColor: cfg.bg }}
            >
              {isPastDate && !isActive ? <Lock size={10} weight="bold" /> : <Ic size={12} weight={isActive ? 'fill' : 'regular'} />}
              {compact ? cfg.label.slice(0, 4) : cfg.label}
            </button>
          );
        })}
      </div>

      {/* Atendimentos */}
      {atendimentos.length > 0 && (
        <div className="space-y-1.5">
          {atendimentos.map(a => {
            const sit = (a.situacao || '').toLowerCase();
            const bgRow = sit === 'compareceu' ? 'bg-[#DCFCE7]' : sit === 'faltou' ? 'bg-[#FEE2E2]' : 'bg-[#F7F6F4]';
            return (
            <div key={a.id} data-testid={`atend-${a.id}`}
              className={`flex items-center gap-2 p-2 rounded-lg group ${bgRow}`}
            >
              <span className="text-[11px] font-medium text-[#A1A1AA] w-10 flex-shrink-0">{a.hora_inicio}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-medium text-[#18181B] truncate">{a.cliente_nome}</p>
                  {a.retroativo && <RetroativoBadge justificativa={a.retroativo_justificativa} />}
                </div>
                {a.cliente_telefone && (
                  <p className="text-[10px] text-[#A1A1AA] truncate">{a.cliente_telefone}</p>
                )}
                {a.servicos?.length > 0 && (
                  <p className="text-[10px] text-[#71717A] truncate">{a.servicos.join(', ')}</p>
                )}
              </div>
              {/* Situacao operacional — sempre disponivel (compareceu / faltou) */}
              <div className="flex gap-0.5 flex-shrink-0">
                <button
                  data-testid={`btn-comp-${a.id}`}
                  onClick={() => onSituacao && onSituacao(a.id, sit === 'compareceu' ? '' : 'compareceu')}
                  title={sit === 'compareceu' ? 'Compareceu (clique para limpar)' : 'Marcar como Compareceu'}
                  className={`w-6 h-6 rounded flex items-center justify-center transition-all ${sit === 'compareceu' ? 'bg-[#22C55E] text-white shadow-sm' : 'text-[#22C55E] hover:bg-white'}`}
                >
                  <CheckCircle size={13} weight={sit === 'compareceu' ? 'fill' : 'regular'} />
                </button>
                <button
                  data-testid={`btn-falt-${a.id}`}
                  onClick={() => onSituacao && onSituacao(a.id, sit === 'faltou' ? '' : 'faltou')}
                  title={sit === 'faltou' ? 'Faltou (clique para limpar)' : 'Marcar como Faltou'}
                  className={`w-6 h-6 rounded flex items-center justify-center transition-all ${sit === 'faltou' ? 'bg-[#EF4444] text-white shadow-sm' : 'text-[#EF4444] hover:bg-white'}`}
                >
                  <XCircle size={13} weight={sit === 'faltou' ? 'fill' : 'regular'} />
                </button>
              </div>
              {!isPastDate && (
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`btn-edit-atend-${a.id}`} onClick={() => onEditAtend(a)}
                    className="w-6 h-6 rounded flex items-center justify-center text-[#A1A1AA] hover:text-[#18181B] hover:bg-white">
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  <button data-testid={`btn-del-atend-${a.id}`} onClick={() => onDeleteAtend(a.id)}
                    className="w-6 h-6 rounded flex items-center justify-center text-[#A1A1AA] hover:text-red-500 hover:bg-white">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== PAST DATE BANNER ====================
const PastDateBanner = ({ onAjusteAtendimento, onAjusteStatus }) => (
  <div
    data-testid="past-date-banner"
    className="mb-5 p-4 rounded-2xl border border-dashed flex flex-wrap items-center justify-between gap-3 animate-fade-in"
    style={{ borderColor: '#C9A96240', backgroundColor: '#C9A96208' }}
  >
    <div className="flex items-center gap-3">
      <Lock size={18} weight="fill" style={{ color: '#C9A962' }} />
      <div>
        <p className="text-sm font-medium" style={{ color: '#8B7340' }}>Data encerrada</p>
        <p className="text-xs text-[#A1A1AA]">Operacoes normais bloqueadas. Use ajuste gerencial para correcoes.</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button
        data-testid="btn-ajuste-status"
        onClick={onAjusteStatus}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all hover:shadow-sm"
        style={{ borderColor: '#C9A96240', color: '#C9A962', backgroundColor: '#C9A96210' }}
      >
        <ShieldCheck size={14} weight="bold" />
        Ajustar Status
      </button>
      <button
        data-testid="btn-ajuste-atendimento"
        onClick={onAjusteAtendimento}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white transition-all hover:shadow-lg"
        style={{ backgroundColor: '#C9A962' }}
      >
        <ShieldCheck size={14} weight="bold" />
        Ajustar Atendimento
      </button>
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================
export const CodAgenda = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [painel, setPainel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfModal, setShowProfModal] = useState(false);
  const [showAtendForm, setShowAtendForm] = useState(false);
  const [editAtend, setEditAtend] = useState(null);
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [ajusteMode, setAjusteMode] = useState('agendamento');
  const [showHistorico, setShowHistorico] = useState(false);

  const fetchPainel = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/cod/painel/${selectedDate}`);
      const data = await res.json();
      setPainel(data);
    } catch { setPainel(null); } finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { fetchPainel(); }, [fetchPainel]);

  // Compute if selected date is in the past
  const isPastDate = (() => {
    try {
      const target = new Date(selectedDate + 'T12:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      target.setHours(0, 0, 0, 0);
      return target < today;
    } catch { return false; }
  })();

  const changeDate = (delta) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleDiaTipo = async (tipo) => {
    if (isPastDate) return;
    await fetch(`${API}/cod/dia/${selectedDate}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: selectedDate, tipo }),
    });
    fetchPainel();
  };

  const handleStatusChange = async (profId, status) => {
    if (isPastDate) return;
    const res = await fetch(`${API}/cod/status-diario`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profissional_id: profId, data: selectedDate, status }),
    });
    if (!res.ok) {
      const err = await res.json();
      if (err?.detail?.code === 'TIME_BLOCKED') {
        return; // silently handled — UI already shows lock
      }
    }
    fetchPainel();
  };

  const handleDeleteAtend = async (id) => {
    if (isPastDate) return;
    if (!window.confirm('Excluir este atendimento?')) return;
    const res = await fetch(`${API}/cod/agendamentos/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json();
      if (err?.detail?.code === 'TIME_BLOCKED') {
        alert(err.detail.message);
        return;
      }
    }
    fetchPainel();
  };

  const handleEditAtend = (atend) => {
    if (isPastDate) return;
    setEditAtend(atend);
    setShowAtendForm(true);
  };

  const handleSituacao = async (agendId, situacao) => {
    // Registro operacional — nao bloqueia por governanca de tempo
    try {
      await fetch(`${API}/cod/agendamentos/${agendId}/situacao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ situacao }),
      });
      fetchPainel();
    } catch { /* silent */ }
  };

  const openAjuste = (mode) => {
    setAjusteMode(mode);
    setShowAjusteModal(true);
  };

  const dia = painel?.dia || { tipo: 'operacional' };
  const equipe = painel?.equipe || [];
  const resumo = painel?.resumo || {};
  const todayFlag = isToday(selectedDate);

  const profissionais = equipe.filter(e => (e.categoria || 'profissional') === 'profissional');
  const auxiliares = equipe.filter(e => e.categoria === 'auxiliar');
  const estagiarios = equipe.filter(e => e.categoria === 'estagiario');

  const isFeriado = dia.tipo === 'feriado' || dia.tipo === 'folga';

  // Get current time position for now indicator
  const getNowMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="cod-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">COD</span>
          </p>
        </div>

        {/* Title */}
        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <ClipboardText size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">COD</h1>
                  <span className="salonup-badge">OPERACIONAL</span>
                </div>
                <p className="salonup-text-sm mt-1">Controle Operacional de Disponibilidade</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="btn-historico" onClick={() => setShowHistorico(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
              ><ClockCounterClockwise size={16} weight="bold" />Historico Operacional</button>
              <button data-testid="btn-equipe" onClick={() => setShowProfModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
              ><UsersThree size={16} weight="bold" />Equipe</button>
              {!isFeriado && !isPastDate && (
                <button data-testid="btn-novo-atendimento"
                  onClick={() => { setEditAtend(null); setShowAtendForm(true); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
                  style={{ backgroundColor: '#1B4332' }}
                ><Plus size={16} weight="bold" />Atendimento</button>
              )}
            </div>
          </div>
        </section>

        {/* Date Navigation */}
        <section className="mb-5 animate-fade-in-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button data-testid="btn-prev-day" onClick={() => changeDate(-1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all"
              ><CaretLeft size={16} weight="bold" /></button>
              <div className="text-center min-w-[240px]">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-lg font-semibold text-[#18181B] capitalize" style={{ fontFamily: 'Outfit' }} data-testid="current-date">
                    {formatDateBR(selectedDate)}
                  </h2>
                  {isPastDate && <Lock size={14} weight="fill" style={{ color: '#C9A962' }} />}
                </div>
                {todayFlag && <span className="text-[10px] font-semibold text-[#1B4332] uppercase tracking-wide">Hoje</span>}
              </div>
              <button data-testid="btn-next-day" onClick={() => changeDate(1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all"
              ><CaretRight size={16} weight="bold" /></button>
            </div>
            <div className="flex items-center gap-2">
              {!todayFlag && (
                <button data-testid="btn-hoje" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  className="text-xs font-medium text-[#1B4332] hover:underline mr-2"
                >Ir para hoje</button>
              )}
              <input data-testid="date-picker" type="date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
          </div>
        </section>

        {/* Day Type Selector */}
        <section className="mb-5 animate-fade-in-up">
          <div className={`flex gap-2 p-1 bg-white border border-[#E4E4E7] rounded-xl w-fit ${isPastDate ? 'opacity-60 pointer-events-none' : ''}`} data-testid="dia-tipo-selector">
            {Object.entries(DIA_TIPOS).map(([key, cfg]) => {
              const isActive = dia.tipo === key;
              const DiaIcon = key === 'feriado' ? CalendarX : key === 'folga' ? CalendarX : Calendar;
              return (
                <button key={key} data-testid={`btn-dia-${key}`}
                  onClick={() => handleDiaTipo(key)}
                  disabled={isPastDate}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive ? 'text-white shadow-sm' : 'hover:bg-[#F4F4F5]'
                  }`}
                  style={isActive ? { backgroundColor: cfg.color } : { color: cfg.color }}
                >
                  <DiaIcon size={14} weight={isActive ? 'fill' : 'regular'} />
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Past Date Banner */}
        {isPastDate && !loading && !isFeriado && equipe.length > 0 && (
          <PastDateBanner
            onAjusteAtendimento={() => openAjuste('agendamento')}
            onAjusteStatus={() => openAjuste('status')}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isFeriado ? (
          /* FERIADO / FOLGA */
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <CalendarX size={40} weight="light" className="mx-auto mb-4" style={{ color: DIA_TIPOS[dia.tipo]?.color }} />
            <h3 className="text-xl font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>
              {DIA_TIPOS[dia.tipo]?.label}
            </h3>
            <p className="text-sm text-[#71717A]">Salao sem operacao neste dia.</p>
          </div>
        ) : equipe.length === 0 ? (
          /* EMPTY STATE */
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <UsersThree size={36} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>Cadastre a equipe</h3>
            <p className="text-sm text-[#71717A] mb-4">Adicione os membros do salao para registrar a operacao.</p>
            <button onClick={() => setShowProfModal(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: '#1B4332' }}
            ><Plus size={16} weight="bold" />Cadastrar equipe</button>
          </div>
        ) : (
          <>
            {/* RESUMO OPERACIONAL */}
            <section className="mb-6 animate-fade-in-up" data-testid="resumo-operacional">
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'produziu', count: resumo.produziu, icon: Lightning },
                  { key: 'disponivel', count: resumo.disponivel, icon: CheckCircle },
                  { key: 'ausente', count: resumo.ausente, icon: XCircle },
                ].map(({ key, count, icon: Ic }) => {
                  const cfg = STATUS_OPERACIONAL[key];
                  return (
                    <div key={key} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-xl" data-testid={`resumo-${key}`}>
                      <Ic size={16} weight="fill" style={{ color: cfg.color }} />
                      <span className="text-lg font-semibold" style={{ color: cfg.color, fontFamily: 'Outfit' }}>{count || 0}</span>
                      <span className="text-xs text-[#71717A]">{cfg.label.toLowerCase()}</span>
                    </div>
                  );
                })}
                {resumo.sem_status > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-dashed border-[#D4D4D8] rounded-xl">
                    <Minus size={16} weight="regular" className="text-[#A1A1AA]" />
                    <span className="text-lg font-semibold text-[#A1A1AA]" style={{ fontFamily: 'Outfit' }}>{resumo.sem_status}</span>
                    <span className="text-xs text-[#A1A1AA]">sem status</span>
                  </div>
                )}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-xl">
                  <UserCircle size={16} weight="regular" className="text-[#71717A]" />
                  <span className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{resumo.total_atendimentos || 0}</span>
                  <span className="text-xs text-[#71717A]">atendimentos</span>
                </div>
              </div>
            </section>

            {/* PROFISSIONAIS */}
            {profissionais.length > 0 && (
              <section className="mb-6 animate-fade-in-up">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">Profissionais</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="grid-profissionais">
                  {profissionais.map(p => (
                    <PersonCard key={p.id} person={p} onStatusChange={handleStatusChange}
                      onDeleteAtend={handleDeleteAtend} onEditAtend={handleEditAtend}
                      onSituacao={handleSituacao}
                      isPastDate={isPastDate} />
                  ))}
                </div>
              </section>
            )}

            {/* AUXILIARES */}
            {auxiliares.length > 0 && (
              <section className="mb-6 animate-fade-in-up">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">Auxiliares</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="grid-auxiliares">
                  {auxiliares.map(p => (
                    <PersonCard key={p.id} person={p} compact onStatusChange={handleStatusChange}
                      onDeleteAtend={handleDeleteAtend} onEditAtend={handleEditAtend}
                      onSituacao={handleSituacao}
                      isPastDate={isPastDate} />
                  ))}
                </div>
              </section>
            )}

            {/* ESTAGIARIOS */}
            {estagiarios.length > 0 && (
              <section className="mb-6 animate-fade-in-up">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">Estagiarios</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="grid-estagiarios">
                  {estagiarios.map(p => (
                    <PersonCard key={p.id} person={p} compact onStatusChange={handleStatusChange}
                      onDeleteAtend={handleDeleteAtend} onEditAtend={handleEditAtend}
                      onSituacao={handleSituacao}
                      isPastDate={isPastDate} />
                  ))}
                </div>
              </section>
            )}

            {/* GRADE HOLISTICA DO DIA */}
            {equipe.length > 0 && (
              <section className="mb-6 animate-fade-in-up" data-testid="grade-holistica">
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Grade do Dia</p>
                  {isPastDate && <Lock size={10} weight="fill" style={{ color: '#C9A962' }} />}
                </div>
                <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
                  <div className="overflow-x-auto">
                    <div className="min-w-[600px]">
                      {/* Column Headers */}
                      <div className="flex border-b border-[#E4E4E7] sticky top-0 z-10 bg-white">
                        <div className="w-14 flex-shrink-0 p-2.5 border-r border-[#E4E4E7]">
                          <span className="text-[9px] font-medium text-[#A1A1AA] uppercase">Hora</span>
                        </div>
                        {equipe.map(person => {
                          const st = STATUS_OPERACIONAL[person.status_dia];
                          return (
                            <div key={person.id} className="flex-1 min-w-[140px] p-2.5 border-r border-[#E4E4E7] last:border-r-0">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
                                  style={{ backgroundColor: person.cor || '#1B4332' }}>
                                  {person.nome?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <span className="text-xs font-medium text-[#18181B] truncate block" style={{ fontFamily: 'Outfit' }}>{person.nome}</span>
                                  <span className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: st?.color || '#A1A1AA' }}>
                                    {st?.label || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Time Grid */}
                      <div className="flex relative" style={{ height: (HORARIO_FIM - HORARIO_INICIO) * SLOT_HEIGHT * 2 }}>
                        {/* Time Labels */}
                        <div className="w-14 flex-shrink-0 border-r border-[#E4E4E7] relative">
                          {generateTimeSlots().filter((_, i) => i % 2 === 0).map(time => (
                            <div key={time} className="absolute w-full flex items-start justify-end pr-2 pt-1" style={{ top: getSlotTop(time), height: SLOT_HEIGHT }}>
                              <span className="text-[10px] font-medium text-[#A1A1AA]">{time}</span>
                            </div>
                          ))}
                        </div>

                        {/* Person Columns */}
                        {equipe.map(person => {
                          const personAtends = person.atendimentos || [];
                          const st = STATUS_OPERACIONAL[person.status_dia];
                          return (
                            <div key={person.id} className="flex-1 min-w-[140px] border-r border-[#E4E4E7] last:border-r-0 relative">
                              {/* Slot lines */}
                              {generateTimeSlots().map((time, i) => {
                                const isSlotPast = isPastDate || (todayFlag && (() => {
                                  const [h, m] = time.split(':').map(Number);
                                  return (h * 60 + m) < getNowMinutes();
                                })());

                                return (
                                  <div key={time}
                                    onClick={() => {
                                      if (!isPastDate && !isSlotPast) {
                                        setEditAtend(null);
                                        setShowAtendForm(true);
                                      }
                                    }}
                                    className={`absolute w-full transition-colors ${
                                      i % 2 === 0 ? 'border-t border-[#E4E4E7]' : 'border-t border-[#E4E4E7]/40'
                                    } ${isSlotPast ? 'bg-[#F7F6F4]/60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#1B4332]/[0.03]'}`}
                                    style={{ top: getSlotTop(time), height: SLOT_HEIGHT }}
                                  />
                                );
                              })}

                              {/* Now indicator */}
                              {todayFlag && (() => {
                                const nowMins = getNowMinutes();
                                const startMins = HORARIO_INICIO * 60;
                                const endMins = HORARIO_FIM * 60;
                                if (nowMins >= startMins && nowMins <= endMins) {
                                  const top = ((nowMins - startMins) / 30) * SLOT_HEIGHT;
                                  return <div className="absolute w-full h-[2px] bg-red-400 z-10 pointer-events-none" style={{ top }} />;
                                }
                                return null;
                              })()}

                              {/* Atendimento cards */}
                              {personAtends.map(a => {
                                const top = getSlotTop(a.hora_inicio);
                                const height = a.hora_fim ? getSlotHeight(a.hora_inicio, a.hora_fim) : SLOT_HEIGHT;
                                const isRetro = a.retroativo;
                                return (
                                  <div key={a.id} data-testid={`grade-atend-${a.id}`}
                                    className={`absolute left-1 right-1 rounded-lg px-2 py-1 transition-all hover:shadow-md z-20 overflow-hidden ${isPastDate ? 'cursor-default' : 'cursor-pointer'}`}
                                    style={{
                                      top: top + 1,
                                      height: Math.max(height - 2, 28),
                                      backgroundColor: isRetro ? '#C9A96215' : (st?.bg || '#F0FDF4'),
                                      borderLeft: `3px solid ${isRetro ? '#C9A962' : (st?.color || '#1B4332')}`,
                                    }}
                                    onClick={(e) => { e.stopPropagation(); if (!isPastDate) handleEditAtend(a); }}
                                  >
                                    <div className="flex items-center gap-1">
                                      <p className="text-[11px] font-semibold truncate" style={{ color: isRetro ? '#C9A962' : (st?.color || '#1B4332') }}>
                                        {a.cliente_nome}
                                      </p>
                                      {isRetro && <ClockCounterClockwise size={10} weight="bold" style={{ color: '#C9A962' }} />}
                                    </div>
                                    {height > 34 && a.servicos?.length > 0 && (
                                      <p className="text-[10px] text-[#71717A] truncate">{a.servicos.join(', ')}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
          <p className="salonup-text-xs">Gestao (CORE) &middot; COD &middot; Painel Operacional</p>
        </footer>
      </div>

      {/* Modals */}
      {showProfModal && (
        <ProfissionaisModal
          profissionais={equipe}
          onClose={() => setShowProfModal(false)}
          onRefresh={fetchPainel}
        />
      )}
      {showAtendForm && (
        <AtendimentoForm
          profissionais={equipe}
          selectedDate={selectedDate}
          editData={editAtend}
          onClose={() => { setShowAtendForm(false); setEditAtend(null); }}
          onSuccess={() => { setShowAtendForm(false); setEditAtend(null); fetchPainel(); }}
        />
      )}
      {showAjusteModal && (
        <AjusteGerencialModal
          selectedDate={selectedDate}
          profissionais={equipe}
          mode={ajusteMode}
          onClose={() => setShowAjusteModal(false)}
          onSuccess={() => { setShowAjusteModal(false); fetchPainel(); }}
        />
      )}
      {showHistorico && (
        <HistoricoOperacionalDrawer
          profissionais={equipe}
          onClose={() => setShowHistorico(false)}
        />
      )}
    </div>
  );
};

export default CodAgenda;
